import React, { useState, useContext, useEffect, useMemo } from 'react'
import { useAppState } from '../../hooks'
import api from '../../api-client'
import { toast } from 'react-toastify'
import { AirtableWebhook, AirtableWebhookResponse, errorToString } from '@oneethos/shared'
import { AppContext } from '../../appContext'
import actions from '../../actions'
import { CreateWebhookForm } from './create-webhook-form'

type WebhookWithStatus = AirtableWebhook & {
  status?: AirtableWebhookResponse
}

const WebhookStatusDetails = ({ 
  status, 
  storedCursor 
}: { 
  status?: AirtableWebhookResponse
  storedCursor?: number
}) => {
  if (!status) {
    return (
      <div className="text-muted small">
        No status information available
      </div>
    )
  }

  const lastResult = status.lastNotificationResult
  const cursorForNextPayload = status.cursorForNextPayload
  const isCursorBehind = storedCursor !== undefined && 
                         cursorForNextPayload !== undefined && 
                         (cursorForNextPayload - storedCursor) > 100

  return (
    <div className="mt-3 pt-3 border-top">
      <h6 className="small text-muted mb-2">Status Details</h6>
      {isCursorBehind && (
        <div className="alert alert-warning small mb-2" role="alert">
          <strong>Warning:</strong> Cursor is behind by {cursorForNextPayload - storedCursor} positions. 
          The stored cursor ({storedCursor}) is significantly behind the next payload cursor ({cursorForNextPayload}).
        </div>
      )}
      <div className="row g-2">
        {cursorForNextPayload !== undefined && (
          <div className="col-md-6">
            <div className="small">
              <strong>Cursor for Next Payload:</strong> {cursorForNextPayload}
            </div>
          </div>
        )}
        {status.expirationTime && (
          <div className="col-md-6">
            <div className="small">
              <strong>Expiration (if not renewed):</strong> {new Date(status.expirationTime).toLocaleString()}
            </div>
          </div>
        )}
        {lastResult && (
          <>
            <div className="col-md-6">
              <div className="small">
                <strong>Last Notification:</strong>{' '}
                <span className={`badge ${lastResult.success ? 'bg-success' : 'bg-danger'}`}>
                  {lastResult.success ? 'Success' : 'Failed'}
                </span>
              </div>
              {lastResult.completionTimestamp && (
                <div className="small text-muted">
                  {new Date(lastResult.completionTimestamp).toLocaleString()}
                </div>
              )}
            </div>
            {lastResult.error && (
              <div className="col-12">
                <div className="small text-danger">
                  <strong>Error:</strong> {lastResult.error.message}
                </div>
              </div>
            )}
            {lastResult.retryNumber !== undefined && (
              <div className="col-md-6">
                <div className="small">
                  <strong>Retry Number:</strong> {lastResult.retryNumber}
                  {lastResult.willBeRetried && (
                    <span className="badge bg-warning text-dark ms-2">Will Retry</span>
                  )}
                </div>
              </div>
            )}
            {lastResult.durationMs !== undefined && (
              <div className="col-md-6">
                <div className="small">
                  <strong>Duration:</strong> {lastResult.durationMs.toFixed(2)}ms
                </div>
              </div>
            )}
          </>
        )}
        {!lastResult && status.lastSuccessfulNotificationTime && (
          <div className="col-md-6">
            <div className="small">
              <strong>Last Successful:</strong> {new Date(status.lastSuccessfulNotificationTime).toLocaleString()}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export const AirtableWebhooks = () => {
  const { tenant: { config: tenant } } = useAppState()
  const { dispatch } = useContext(AppContext)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [webhooksWithStatus, setWebhooksWithStatus] = useState<WebhookWithStatus[]>([])
  const [loading, setLoading] = useState(true)
  const baseWebhooks = useMemo(() => tenant?.airtableWebhooks || [], [tenant?.airtableWebhooks])
  const webhookIds = useMemo(() => baseWebhooks.map(w => w.id).join(','), [baseWebhooks])

  useEffect(() => {
    const fetchWebhooks = () => {
      setLoading(true)
      api.get('/tenant/airtable-webhooks')
        .then((webhooks: WebhookWithStatus[]) => {
          setWebhooksWithStatus(webhooks)
        })
        .catch(ex => {
          toast.error(errorToString(ex))
          // Fallback to base webhooks if fetch fails
          setWebhooksWithStatus(baseWebhooks.map(w => ({ ...w })))
        })
        .finally(() => setLoading(false))
    }

    if (baseWebhooks.length > 0) {
      fetchWebhooks()
    } else {
      setWebhooksWithStatus([])
      setLoading(false)
    }
  }, [webhookIds, baseWebhooks])

  const handleDelete = (webhook: AirtableWebhook) => {
    if (!window.confirm(`Are you sure you want to delete the webhook for ${webhook.notificationUrl}? This action cannot be undone.`)) {
      return
    }

    api.delete(`/tenant/airtable-webhooks/${webhook.id}`)
      .then((res) => {
        toast.success('Webhook deleted successfully')
        dispatch(actions.setTenant(res))
        // Refetch webhooks after delete
        setLoading(true)
        api.get('/tenant/airtable-webhooks')
          .then((webhooks: WebhookWithStatus[]) => {
            setWebhooksWithStatus(webhooks)
          })
          .catch(ex => {
            toast.error(errorToString(ex))
          })
          .finally(() => setLoading(false))
      })
      .catch(ex => {
        toast.error(errorToString(ex))
      })
  }

  const handleWebhookCreated = () => {
    // Refresh tenant data to get the updated webhook list
    api.get('/tenant')
      .then((res) => {
        dispatch(actions.setTenant(res))
        setShowCreateForm(false)
        // Refetch webhooks after create
        setLoading(true)
        api.get('/tenant/airtable-webhooks')
          .then((webhooks: WebhookWithStatus[]) => {
            setWebhooksWithStatus(webhooks)
          })
          .catch(ex => {
            toast.error(errorToString(ex))
          })
          .finally(() => setLoading(false))
      })
      .catch(ex => {
        toast.error(errorToString(ex))
      })
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Airtable Webhooks</h4>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Webhook'}
        </button>
      </div>

      {showCreateForm && (
        <CreateWebhookForm
          onWebhookCreated={handleWebhookCreated}
        />
      )}

      {loading ? (
        <div className="alert alert-info" role="alert">
          Loading webhook status...
        </div>
      ) : webhooksWithStatus.length === 0 ? (
        <div className="alert alert-light" role="alert">
          No webhooks configured. Create a webhook to start receiving Airtable notifications.
        </div>
      ) : (
        <div className="list-group">
          {webhooksWithStatus.map((webhook) => {
            const status = webhook.status
            const isEnabled = status?.isHookEnabled ?? false
            const notificationsEnabled = status?.areNotificationsEnabled ?? false
            const storedCursor = webhook.cursor ?? 0
            
            return (
              <div key={webhook.id} className="list-group-item mb-3 border rounded">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span className={`badge ${isEnabled ? 'bg-success' : 'bg-secondary'}`}>
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      {notificationsEnabled && (
                        <span className="badge bg-info">Notifications On</span>
                      )}
                    </div>
                    
                    <div className="mb-2">
                      <div className="small">
                        <strong>Base ID:</strong> {webhook.baseId}
                      </div>
                      <div className="small">
                        <strong>Webhook ID:</strong>{' '}
                        <code className="small">{webhook.id}</code>
                      </div>
                      <div className="small">
                        <strong>Notification URL:</strong>{' '}
                        <a href={webhook.notificationUrl} target="_blank" rel="noopener noreferrer">
                          {webhook.notificationUrl}
                        </a>
                      </div>
                      <div className="small">
                        <strong>Stored Cursor:</strong> {storedCursor}
                      </div>
                    </div>

                    <WebhookStatusDetails status={status} storedCursor={storedCursor} />
                  </div>
                  
                  <div className="ms-3">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => handleDelete(webhook)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

